import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@os/components/ui/button";
import { Card } from "@os/components/ui/card";
import { Input } from "@os/components/ui/input";
import { Textarea } from "@os/components/ui/textarea";
import { Save, FolderOpen, Sparkles, Plus, Minus, Monitor } from "lucide-react";

import { useToast } from "@os/hooks/use-toast";
import { useAuth } from "@os/hooks/useAuth";
import { supabase } from "@os/integrations/supabase/client";
import { DraggableFileDialog } from "@os/components/DraggableFileDialog";
import RichTextEditor from "@/components/ui/RichTextEditor";
import MapleSlideGenerator from "./MapleSlideGenerator";
import MapleSlidePresenter from "./MapleSlidePresenter";
import MapleWordStatusBar from "./MapleWordStatusBar";
import { markdownToHtml, htmlToMarkdown, splitDeck, joinDeck, extractSlideTitle, parseSlideMeta, splitTwoColumnBody, markdownBodyToHtml } from "./SlideMarkdownUtils";
import SlideFormattingBar from "./SlideFormattingBar";
import SlideTemplateModal from "./SlideTemplateModal";
import { 
  Select, 
  SelectTrigger, 
  SelectValue, 
  SelectContent, 
  SelectItem 
} from "@os/components/ui/select";
import { Slider } from "@os/components/ui/slider";
import { ensureDir } from "@os/utils/filesystem";

interface MapleSlideProps {
  filePath?: string;
  initialContent?: string; // now expected to be Markdown (backwards-compatible with JSON if present)
}

export const MapleSlide: React.FC<MapleSlideProps> = ({ filePath, initialContent }) => {
  const { toast } = useToast();
  const { user } = useAuth();

  // Deck state: full deck markdown + per-slide markdown
  const [deckMd, setDeckMd] = useState<string>(() =>
    initialContent ? "" : "# Welcome\n\n- Create slides with Markdown\n\n---\n\n# Agenda\n\n- Intro\n- Demo\n- Q&A"
  );
  const [slidesMd, setSlidesMd] = useState<string[]>(() =>
    initialContent ? [""] : splitDeck(deckMd)
  );
  const [current, setCurrent] = useState(0);

  // File state
  const [currentFilePath, setCurrentFilePath] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveFileName, setSaveFileName] = useState("");
  const [showGenerator, setShowGenerator] = useState(false);
  const [showPresenter, setShowPresenter] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // TipTap content (HTML) for current slide
  const [currentHtml, setCurrentHtml] = useState<string>("");
  const editorRef = useRef<any>(null); // single-editor ref (non two-column)
  const editorLeftRef = useRef<any>(null);
  const editorRightRef = useRef<any>(null);
  const [leftHtml, setLeftHtml] = useState<string>("");
  const [rightHtml, setRightHtml] = useState<string>("");

  // NEW: desktop image upload state & ref
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // NEW: track whether current slide has unsaved edits
  const [isSlideDirty, setIsSlideDirty] = useState(false);

  // Title of deck
  const [title, setTitle] = useState<string>("Untitled Presentation");

  // NEW: external revision key to signal TipTap to re-sync content
  const [mdRevision, setMdRevision] = useState(0);

  // NEW: key used to force RichTextEditor to re-sync content only after HTML is ready
  const [contentSyncKey, setContentSyncKey] = useState(0);

  // FIX: declare these early so effects can safely reference them
  const [osUsername, setOsUsername] = useState<string>("User");
  const [usernameReady, setUsernameReady] = useState<boolean>(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // NEW: ensure only the latest HTML generation applies (prevents stale overwrite)
  const htmlSyncSeq = useRef(0);

  // Show "Editing" until the next save/autosave completes
  useEffect(() => {
    setIsSaved(false);
  }, [deckMd]);

  // Fallback counters for status bar when editor storage isn't ready
  const wordsFallback = useMemo(() => {
    const html = currentHtml || "";
    const plain = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (plain.length > 0) {
      return plain.split(" ").filter(Boolean).length;
    }
    const md = slidesMd[current] || "";
    return md.split(/\s+/).filter(Boolean).length;
  }, [currentHtml, slidesMd, current]);

  const charsFallback = useMemo(() => {
    const html = currentHtml || "";
    const plain = html.replace(/<[^>]+>/g, "");
    if (plain.trim().length > 0) {
      return plain.length;
    }
    const md = slidesMd[current] || "";
    return md.length;
  }, [currentHtml, slidesMd, current]);

  // Active editor for toolbar actions (left if focused, else right, else single editor)
  const activeEditor = (editorLeftRef.current?.isFocused && editorLeftRef.current)
    || (editorRightRef.current?.isFocused && editorRightRef.current)
    || editorRef.current
    || editorLeftRef.current
    || editorRightRef.current;

  // Helper: persist current editor content to slidesMd/deckMd
  const persistCurrentSlide = () => {
    // Only persist if the slide actually has unsaved edits
    if (!isSlideDirty) return;
    const slideMd = slidesMd[current] || "";
    const meta = parseSlideMeta(slideMd);
    if (meta.layout === "two-column") {
      const leftMd = htmlToMarkdown(editorLeftRef.current?.getHTML?.() || leftHtml || "");
      const rightMd = htmlToMarkdown(editorRightRef.current?.getHTML?.() || rightHtml || "");
      rebuildTwoColumnSlide(leftMd, rightMd);
    } else {
      const html = editorRef.current?.getHTML?.() ?? currentHtml;
      if (!html) {
        setIsSlideDirty(false);
        return;
      }
      const md = htmlToMarkdown(html);
      setSlidesMd((prev) => {
        const next = [...prev];
        next[current] = md;
        setDeckMd(joinDeck(next));
        return next;
      });
    }
    setIsSlideDirty(false);
  };

  // NEW: unified slide selection logic (same as clicking a slide tile)
  const selectSlide = (index: number, forceRemount: boolean = false) => {
    persistCurrentSlide();
    // Always bump to guarantee a fresh sync on any selection (including the same slide)
    setContentSyncKey((k) => k + 1);
    setCurrent(index);
  };

  // REPLACE: URL-based insert with desktop picker
  const onAddImageFromDesktop = () => {
    imageInputRef.current?.click();
  };

  const handleImageFileSelected = async (file: File) => {
    setIsUploadingImage(true);
    try {
      const BUCKET = 'rich-text-images';
      const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
      const safeFileName = `slide-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`.replace(/[^a-z0-9.\-_]/gi, '_');

      const { error } = await supabase.storage
        .from(BUCKET)
        .upload(safeFileName, file, {
          upsert: true,
          contentType: file.type || 'image/png',
          cacheControl: '3600',
        });

      if (error) throw error;

      const { data: publicUrlData } = supabase.storage.from(BUCKET).getPublicUrl(safeFileName);
      const url = publicUrlData?.publicUrl;
      if (!url) throw new Error('Could not get public URL for uploaded image.');

      const targetEd = activeEditor || editorRef.current || editorLeftRef.current || editorRightRef.current;
      targetEd?.chain().focus().setImage({ src: url }).run();
      toast({ title: "Image uploaded", description: "Hosted on Supabase and inserted." });
    } catch (err: any) {
      console.error("Slide image upload failed:", err);
      toast({
        title: "Upload failed",
        description: err.message || "Could not upload image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingImage(false);
    }
  };

  // NEW: resolve OS username similar to MapleWord
  useEffect(() => {
    if (!user) {
      setUsernameReady(true);
      return;
    }
    let cancelled = false;
    supabase
      .from("profiles")
      .select("username")
      .eq("id", user.id)
      .single()
      .then(({ data }) => {
        if (cancelled) return;
        const fallback = (user.email?.split("@")[0] || "User");
        const candidate = (data?.username || fallback);
        const sanitized = (candidate.replace(/[^a-zA-Z0-9]/g, "") || "User");
        setOsUsername(sanitized);
        setUsernameReady(true);
      })
      .catch(() => {
        const fallback = (user.email?.split("@")[0] || "User");
        const sanitized = (fallback.replace(/[^a-zA-Z0-9]/g, "") || "User");
        setOsUsername(sanitized);
        setUsernameReady(true);
      });
    return () => { cancelled = true; };
  }, [user]);

  // NEW: convenience path builder
  const getUserDocumentsPath = () => `C:\\User\\${osUsername}\\Documents`;

  // NEW: gate initial HTML sync until initialContent is processed
  const [initialLoadDone, setInitialLoadDone] = useState<boolean>(!initialContent);

  // Initialize from props (support old JSON format fallback)
  useEffect(() => {
    if (!initialContent) return;
    (async () => {
      try {
        const trimmed = initialContent.trim();
        if (trimmed.startsWith("{") && trimmed.includes("\"slides\"")) {
          // Legacy JSON deck: convert to simple Markdown deck
          const parsed = JSON.parse(trimmed);
          const mdSlides: string[] = Array.isArray(parsed?.slides)
            ? parsed.slides.map((s: any) => {
                const t = typeof s.title === "string" ? `# ${s.title}\n\n` : "";
                const c = typeof s.content === "string" ? s.content : "";
                const maybeMd = /<\w+/.test(c) ? htmlToMarkdown(c) : c;
                return `${t}${maybeMd}`.trim();
              })
            : [htmlToMarkdown(parsed?.content || "")];
          const mdDeck = joinDeck(mdSlides);

          setDeckMd(mdDeck);
          setSlidesMd(mdSlides);
          setTitle(typeof parsed?.title === "string" ? parsed.title : "Untitled Presentation");
          setCurrentFilePath(filePath || "");
          setMdRevision((r) => r + 1);

          // Immediate HTML sync for first slide so the main editor shows correct content
          const firstHtml = await markdownToHtml(mdSlides[0] || "");
          htmlSyncSeq.current++;
          setCurrentHtml(firstHtml);
          setContentSyncKey((k) => k + 1);

          // Also run the slide selection process to mirror a tile click
          selectSlide(0, true);

          // NEW: prime the editor by simulating a user click sequence (slide 2 → slide 1)
          // This guarantees the same re-sync path as manual clicking.
          setTimeout(() => {
            if (mdSlides.length > 1) {
              selectSlide(1, true);
              setTimeout(() => selectSlide(0, true), 0);
            }
          }, 0);

          // Mark initial load done to allow normal syncing thereafter
          setInitialLoadDone(true);
        } else {
          // Markdown path
          const mdDeck = initialContent;
          const mdSlides = splitDeck(mdDeck);

          setDeckMd(mdDeck);
          setSlidesMd(mdSlides);
          if (filePath) {
            const fileName = filePath.split("\\").pop() || "Untitled.mslide";
            setTitle(fileName.replace(/\.(mslide|mapleslide|md|markdown|txt)$/i, ""));
          }
          setCurrentFilePath(filePath || "");
          setMdRevision((r) => r + 1);

          // Immediate HTML sync for first slide
          const firstHtml = await markdownToHtml(mdSlides[0] || "");
          htmlSyncSeq.current++;
          setCurrentHtml(firstHtml);
          setContentSyncKey((k) => k + 1);

          // Also run the slide selection process to mirror a tile click
          selectSlide(0, true);

          // NEW: prime the editor by simulating a user click sequence (slide 2 → slide 1)
          setTimeout(() => {
            if (mdSlides.length > 1) {
              selectSlide(1, true);
              setTimeout(() => selectSlide(0, true), 0);
            }
          }, 0);

          // Mark initial load done
          setInitialLoadDone(true);
        }
      } catch (e) {
        // Fallback: treat as plain markdown text
        const mdDeck = initialContent;
        const mdSlides = splitDeck(mdDeck);

        setDeckMd(mdDeck);
        setSlidesMd(mdSlides);
        setCurrentFilePath(filePath || "");
        setMdRevision((r) => r + 1);

        // Immediate HTML sync for first slide on fallback
        const firstHtml = await markdownToHtml(mdSlides[0] || "");
        htmlSyncSeq.current++;
        setCurrentHtml(firstHtml);
        setContentSyncKey((k) => k + 1);

        // Also run the slide selection process to mirror a tile click
        selectSlide(0, true);

        // NEW: prime the editor by simulating a user click sequence (slide 2 → slide 1)
        setTimeout(() => {
          if (mdSlides.length > 1) {
            selectSlide(1, true);
            setTimeout(() => selectSlide(0, true), 0);
          }
        }, 0);

        // Mark initial load done
        setInitialLoadDone(true);
      }
    })();
  }, [initialContent, filePath]);

  // Keep editor HTML in sync when slide changes or external deck changes.
  useEffect(() => {
    if (!initialLoadDone) return;
    const seq = ++htmlSyncSeq.current;
    const md = slidesMd[current] || "";
    const meta = parseSlideMeta(md);
    // Avoid re-sync while user is typing in the focused editor(s)
    const isFocused = meta.layout === "two-column"
      ? !!(editorLeftRef.current?.isFocused || editorRightRef.current?.isFocused)
      : !!editorRef.current?.isFocused;
    if (isFocused) return;
    if (meta.layout === "two-column") {
      const parts = splitTwoColumnBody(md) || { left: "", right: "" };
      (async () => {
        const [l, r] = await Promise.all([markdownBodyToHtml(parts.left || ""), markdownBodyToHtml(parts.right || "")]);
        if (seq === htmlSyncSeq.current) {
          setLeftHtml(l);
          setRightHtml(r);
          setContentSyncKey((k) => k + 1);
        }
      })();
    } else {
      (async () => {
        const html = await markdownToHtml(md);
        if (seq === htmlSyncSeq.current) {
          setCurrentHtml(html);
          setContentSyncKey((k) => k + 1);
        }
      })();
    }
  }, [current, mdRevision, initialLoadDone, slidesMd, parseSlideMeta]);

  // Keep slides list in sync when deck Markdown changes
  useEffect(() => {
    const nextSlides = splitDeck(deckMd);
    setSlidesMd((prev) => {
      const unchanged = nextSlides.length === prev.length && nextSlides.every((s, i) => s === prev[i]);
      if (unchanged) return prev;
      if (current >= nextSlides.length) setCurrent(Math.max(0, nextSlides.length - 1));
      return nextSlides;
    });
  }, [deckMd]);

  // Update slide markdown when TipTap changes (do NOT bump mdRevision; it's an internal edit)
  const onTipTapChange = (html: string) => {
    setCurrentHtml(html);
    const md = htmlToMarkdown(html);
    setSlidesMd((prev) => {
      const next = [...prev];
      next[current] = md;
      setDeckMd(joinDeck(next));
      return next;
    });
    // Mark slide as dirty so we only persist when needed
    setIsSlideDirty(true);
  };

  // Two-column change handlers
  const rebuildTwoColumnSlide = (leftMd: string, rightMd: string) => {
    const original = slidesMd[current] || "";
    const headerLines: string[] = [];
    const lines = (original.replace(/\r\n/g, "\n").split("\n"));
    let i = 0;
    while (i < lines.length && lines[i].trim().startsWith("::")) {
      headerLines.push(lines[i]);
      i++;
    }
    if (!headerLines.some((l) => /^::\s*layout\s*=\s*two-column$/i.test(l.trim()))) {
      headerLines.push("::layout=two-column");
    }
    const header = headerLines.length ? headerLines.join("\n") + "\n\n" : "::layout=two-column\n\n";
    const combined = `${header}${leftMd.trim()}\n\n||\n\n${rightMd.trim()}`;
    setSlidesMd((prev) => {
      const next = [...prev];
      next[current] = combined;
      setDeckMd(joinDeck(next));
      return next;
    });
    setIsSlideDirty(true);
  };
  const onLeftChange = (html: string) => {
    setLeftHtml(html);
    const leftMd = htmlToMarkdown(html);
    const rightMd = htmlToMarkdown(editorRightRef.current?.getHTML?.() || rightHtml || "");
    rebuildTwoColumnSlide(leftMd, rightMd);
  };
  const onRightChange = (html: string) => {
    setRightHtml(html);
    const rightMd = htmlToMarkdown(html);
    const leftMd = htmlToMarkdown(editorLeftRef.current?.getHTML?.() || leftHtml || "");
    rebuildTwoColumnSlide(leftMd, rightMd);
  };

  // Insert a new slide after the current one, then select it (with a forced resync).
  const insertSlideAfterCurrent = (md: string) => {
    persistCurrentSlide();
    setSlidesMd((prev) => {
      const insertAt = Math.min(current + 1, prev.length);
      const next = [...prev.slice(0, insertAt), md, ...prev.slice(insertAt)];
      setDeckMd(joinDeck(next));
      // Defer selection to ensure state is applied before syncing editor
      setTimeout(() => selectSlide(insertAt, true), 0);
      return next;
    });
    setMdRevision((r) => r + 1);
    toast({ title: "Slide added", description: "Your new slide has been inserted." });
  };

  // Add / delete slides
  const deleteSlide = () => {
    setSlidesMd((prev) => {
      if (prev.length <= 1) {
        toast({ title: "Cannot delete", description: "A deck must have at least one slide.", variant: "destructive" });
        return prev;
      }
      const next = prev.filter((_, i) => i !== current);
      setDeckMd(joinDeck(next));
      setCurrent(Math.max(0, current - 1));
      return next;
    });
    setMdRevision((r) => r + 1);
  };

  // NEW: ensure editor shows current HTML as soon as it's available (and whenever it changes)
  // useEffect(() => {
  //   const ed = editorRef.current;
  //   if (!ed) return;
  //   if (currentHtml && ed.getHTML() !== currentHtml) {
  //     ed.commands.setContent(currentHtml, false);
  //   }
  // }, [currentHtml, contentSyncKey]);

  // ADD: Guarded sync to avoid caret jumps; only apply when editor isn't focused.
  useEffect(() => {
    // Single editor sync
    const ed = editorRef.current;
    if (ed) {
      const hasHtml = typeof currentHtml === "string" && currentHtml.trim().length > 0;
      if (hasHtml) {
        const editorEmpty = ed.getText().trim().length === 0;
        const htmlDiffers = ed.getHTML() !== currentHtml;
        if (!ed.isFocused && (editorEmpty || htmlDiffers)) {
          ed.commands.setContent(currentHtml, false);
        }
      }
    }
    // Two-column sync
    const l = editorLeftRef.current;
    if (l && leftHtml) {
      const empty = l.getText().trim().length === 0;
      const diff = l.getHTML() !== leftHtml;
      if (!l.isFocused && (empty || diff)) {
        l.commands.setContent(leftHtml, false);
      }
    }
    const r = editorRightRef.current;
    if (r && rightHtml) {
      const empty = r.getText().trim().length === 0;
      const diff = r.getHTML() !== rightHtml;
      if (!r.isFocused && (empty || diff)) {
        r.commands.setContent(rightHtml, false);
      }
    }
  }, [currentHtml, leftHtml, rightHtml, contentSyncKey]);

  const handleLoad = () => setShowFileBrowser(true);

  const handleFileSelect = async (file: any) => {
    if (!file.content) {
      toast({ title: "Error", description: "Selected file has no content.", variant: "destructive" });
      return;
    }
    setIsLoading(true);
    try {
      const content = String(file.content);
      let mdDeck = content;
      // Convert legacy JSON to Markdown if needed
      if (content.trim().startsWith("{")) {
        try {
          const parsed = JSON.parse(content);
          const mdSlides: string[] = Array.isArray(parsed?.slides)
            ? parsed.slides.map((s: any) => {
                const t = typeof s.title === "string" ? `# ${s.title}\n\n` : "";
                const c = typeof s.content === "string" ? s.content : "";
                const maybeMd = /<\w+/.test(c) ? htmlToMarkdown(c) : c;
                return `${t}${maybeMd}`.trim();
              })
            : [htmlToMarkdown(parsed?.content || "")];
          mdDeck = joinDeck(mdSlides);
          setTitle(typeof parsed?.title === "string" ? parsed.title : (file.file_name || "Untitled"));
        } catch {
          // treat as raw Markdown
          mdDeck = content;
        }
      } else {
        setTitle((file.file_name || "Untitled").replace(/\.(mslide|mapleslide|md|markdown|txt)$/i, ""));
      }

      const mdSlides = splitDeck(mdDeck);
      setDeckMd(mdDeck);
      setSlidesMd(mdSlides);
      // Select first slide and force editor remount (same as clicking the slide tile)
      selectSlide(0, true);
      setCurrentFilePath(file.file_path);
      setMdRevision((r) => r + 1);
      // Loaded content should not be immediately persisted back
      setIsSlideDirty(false);

      // NEW: Immediately sync HTML for the first slide so main editor is correct without clicking
      {
        const firstHtml = await markdownToHtml(mdSlides[0] || "");
        // Invalidate any in-flight older conversions and apply immediately
        htmlSyncSeq.current++;
        setCurrentHtml(firstHtml);
        setContentSyncKey((k) => k + 1);
      }

      toast({ title: "Presentation Loaded", description: `Loaded: ${file.file_name}` });
    } catch (error) {
      toast({ title: "Load Failed", description: "Failed to load the presentation.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  // UPDATE: Save logic to use .mslide and MapleWord methodology
  const handleSave = async () => {
    if (currentFilePath) {
      await saveToVFSAndSupabase(currentFilePath);
    } else {
      // default .mslide filename
      const base = title.trim() || "Untitled Presentation";
      const next = base.toLowerCase().endsWith(".mslide") ? base : `${base}.mslide`;
      setSaveFileName(next);
      setShowSaveDialog(true);
    }
  };

  // NEW: explicit Save As handler (always open the Save dialog)
  const handleSaveAs = () => {
    const base = (title || "Untitled Presentation").trim();
    const next = base.toLowerCase().endsWith(".mslide") ? base : `${base}.mslide`;
    setSaveFileName(next);
    setShowSaveDialog(true);
  };

  const handleSaveLocation = async (selectedFolder: any) => {
    const rawName = saveFileName || `${title}.mslide`;
    const fileName = rawName.toLowerCase().endsWith(".mslide")
      ? rawName
      : `${rawName.replace(/\.(mapleslide|md|markdown|txt)$/i, "")}.mslide`;
    const base = (selectedFolder?.path || selectedFolder?.file_path || "C:\\").trim();
    const basePath = base.endsWith("\\") ? base : `${base}\\`;
    const filePath = `${basePath}${fileName}`;

    // NEW: update the UI title to match the saved filename (without extension)
    const newTitle = fileName.replace(/\.(mslide|mapleslide|md|markdown|txt)$/i, "");
    setTitle(newTitle);

    // IMPORTANT: pass parent path without trailing backslash so VFS keys match
    await saveToVFSAndSupabase(filePath, basePath.slice(0, -1));
    setShowSaveDialog(false);
  };

  const saveToVFSAndSupabase = async (path: string, parentPath?: string, opts?: { silent?: boolean }) => {
    if (!opts?.silent) setIsSaving(true);
    try {
      const { globalVFS } = await import("@os/utils/storage/VFS");
      const content = deckMd;
      // Ensure parent directory exists (MapleSlide folders etc.)
      const dir = parentPath || path.substring(0, path.lastIndexOf("\\"));
      await ensureDir(dir);

      await globalVFS.save(path, content);

      window.dispatchEvent(
        new CustomEvent("fileSystemChanged", {
          detail: { path: dir },
        })
      );

      if (user) {
        await saveToSupabase(path, dir, content, "application/x-mapleslide");
      }

      setCurrentFilePath(path);
      setIsSaved(true);
    } catch (error) {
      toast({ title: "Save Failed", description: "Failed to save the presentation. Please try again.", variant: "destructive" });
    } finally {
      if (!opts?.silent) setIsSaving(false);
    }
  };

  const saveToSupabase = async (filePath: string, parentPath?: string, content?: string, contentType?: string) => {
    if (!user) return;
    try {
      const fileName = filePath.split("\\").pop() || "Untitled.mslide";
      const parent = parentPath || filePath.substring(0, filePath.lastIndexOf("\\"));

      const { data: existingFile } = await supabase
        .from("os_user_files")
        .select("id")
        .eq("user_id", user.id)
        .eq("file_path", filePath)
        .maybeSingle();

      let result;
      if (existingFile) {
        result = await supabase
          .from("os_user_files")
          .update({
            content,
            size_bytes: content?.length || 0,
            updated_at: new Date().toISOString(),
            ...(contentType ? { content_type: contentType } : {})
          })
          .eq("id", existingFile.id)
          .eq("user_id", user.id);
      } else {
        result = await supabase.from("os_user_files").insert({
          user_id: user.id,
          file_path: filePath,
          file_name: fileName,
          parent_path: parent,
          content,
          content_type: contentType || "text/markdown",
          size_bytes: content?.length || 0,
          is_directory: false,
        });
      }
      if ((result as any).error) throw (result as any).error;
    } catch (e) {
      throw e;
    }
  };

  // NEW: autosave while typing (debounced), based on deckMd
  useEffect(() => {
    if (!currentFilePath) return;
    const parent = currentFilePath.substring(0, currentFilePath.lastIndexOf("\\"));
    const timer = setTimeout(async () => {
      setAutoSaving(true);
      await saveToVFSAndSupabase(currentFilePath, parent, { silent: true });
      setAutoSaving(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, [deckMd, currentFilePath]);

  // NEW: initial autosave for untitled decks
  useEffect(() => {
    if (currentFilePath) return;
    const hasContent = (deckMd || "").trim().length > 0;
    if (!hasContent || !usernameReady) return;

    const timer = setTimeout(async () => {
      const docs = getUserDocumentsPath();
      const draftsPath = `${docs}\\MapleSlide\\Drafts`;
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const defaultName = "Untitled";
      const filePath = `${draftsPath}\\${defaultName}-${timestamp}.mslide`;
      await ensureDir(draftsPath);
      await saveToVFSAndSupabase(filePath, draftsPath, { silent: true });
    }, 1500);
    return () => clearTimeout(timer);
  }, [deckMd, currentFilePath, usernameReady, osUsername]);

  // Apply AI-generated Markdown deck
  const applyGeneratedDeck = (mode: "replace" | "append", mdDeck: string) => {
    const genSlides = splitDeck(mdDeck);
    if (mode === "replace") {
      setDeckMd(mdDeck);
      setSlidesMd(genSlides);
      setCurrent(0);
    } else {
      setSlidesMd((prev) => {
        const next = [...prev, ...genSlides];
        setDeckMd(joinDeck(next));
        return next;
      });
    }
    setMdRevision((r) => r + 1);
    toast({ title: "Slides generated", description: mode === "replace" ? "Replaced with new deck." : "Appended to current deck." });
  };

  const slideTitles = useMemo(() => slidesMd.map(extractSlideTitle), [slidesMd]);

  // Live slide meta for visual styling of the editor canvas
  const currentMeta = useMemo(() => parseSlideMeta(slidesMd[current] || ""), [slidesMd, current]);
  const editorCanvasStyle = useMemo<React.CSSProperties>(() => {
    const s: React.CSSProperties = {};
    if (currentMeta.bg) s.backgroundColor = currentMeta.bg;
    if (currentMeta.text) s.color = currentMeta.text;
    if (currentMeta.bgImage) {
      s.backgroundImage = `url(${currentMeta.bgImage})`;
      s.backgroundSize = "cover";
      s.backgroundPosition = "center";
    }
    if (currentMeta as any) {
      const align = (currentMeta as any).align as 'left' | 'center' | 'right' | undefined;
      if (align) s.textAlign = align;
    }
    return s;
  }, [currentMeta]);

  return (
    <div className="relative flex flex-col h-full bg-background">
      {/* Header */}
      <div className="border-b border-border p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="max-w-64" placeholder="Presentation title" />
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Slide {current + 1} of {slidesMd.length}</span>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={() => {
            const md = "# New Presentation\n\n- Start\n\n---\n\n# Slide 2\n\n- Content";
            setDeckMd(md);
            const s = splitDeck(md);
            setSlidesMd(s);
            setCurrent(0);
            setTitle("Untitled Presentation");
            setMdRevision((r) => r + 1);
          }} size="sm" variant="outline">
            New
          </Button>
          <Button onClick={handleLoad} size="sm" variant="outline" disabled={isLoading}>
            <FolderOpen className="w-4 h-4 mr-1" />
            {isLoading ? "Loading..." : "Open"}
          </Button>
          <Button onClick={handleSave} size="sm" variant="outline" disabled={isSaving}>
            <Save className="w-4 h-4 mr-1" />
            {isSaving ? "Saving..." : "Save"}
          </Button>
          <Button onClick={handleSaveAs} size="sm" variant="outline" disabled={isSaving}>
            <Save className="w-4 h-4 mr-1" />
            Save As
          </Button>

          <div className="h-6 w-px bg-border mx-2" />

          <Button onClick={() => setShowTemplateModal(true)} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-1" />
            Add Slide
          </Button>
          <Button onClick={deleteSlide} size="sm" variant="outline">
            <Minus className="w-4 h-4 mr-1" />
            Delete
          </Button>

          <div className="h-6 w-px bg-border mx-2" />

          <Button onClick={() => setShowPresenter(true)} size="sm" variant="secondary" title="Present (full-screen)">
            <Monitor className="w-4 h-4 mr-1" />
            Present
          </Button>

          <Button onClick={() => setShowGenerator(true)} size="sm" variant="secondary">
            <Sparkles className="w-4 h-4 mr-1" />
            AI Generate (Markdown)
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Slide Navigation */}
        <div className="w-64 border-r border-border p-4 overflow-y-auto">
          <h3 className="font-medium mb-3">Slides</h3>
          <div className="space-y-2">
            {slideTitles.map((t, i) => (
              <Card
                key={i}
                className={`p-3 cursor-pointer hover:bg-muted transition-colors ${i === current ? "ring-2 ring-orange-500" : ""}`}
                onClick={() => selectSlide(i)}
              >
                <div className="text-sm font-medium truncate">{t || `Slide ${i + 1}`}</div>
              </Card>
            ))}
          </div>
        </div>

        {/* Main Editor + Markdown pane */}
        <div className="flex-1 grid grid-cols-1 xl:grid-cols-2 gap-0">
          {/* TipTap editor for current slide */}
          <div className="h-full p-6 overflow-auto">
            <Card className="h-full p-4 flex flex-col" style={editorCanvasStyle}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-muted-foreground">Visual editor — edit current slide</div>
              </div>

              {/* Ribbon above the editor; routes actions to focused editor */}
              <SlideFormattingBar
                editor={activeEditor}
                onAddImage={onAddImageFromDesktop}
                isUploadingImage={isUploadingImage}
              />

              <div className="flex-1">
                {initialLoadDone ? (
                  parseSlideMeta(slidesMd[current] || "").layout === "two-column" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <RichTextEditor
                        value={leftHtml}
                        onChange={onLeftChange}
                        placeholder="Left column…"
                        variant="default"
                        showToolbar={false}
                        onEditorReady={(ed) => {
                          editorLeftRef.current = ed;
                          if (ed && typeof leftHtml === "string" && leftHtml.trim().length > 0) {
                            const empty = ed.getText().trim().length === 0;
                            if (empty) ed.commands.setContent(leftHtml, false);
                          }
                        }}
                        contentKey={`${current}:L:${contentSyncKey}`}
                      />
                      <RichTextEditor
                        value={rightHtml}
                        onChange={onRightChange}
                        placeholder="Right column…"
                        variant="default"
                        showToolbar={false}
                        onEditorReady={(ed) => {
                          editorRightRef.current = ed;
                          if (ed && typeof rightHtml === "string" && rightHtml.trim().length > 0) {
                            const empty = ed.getText().trim().length === 0;
                            if (empty) ed.commands.setContent(rightHtml, false);
                          }
                        }}
                        contentKey={`${current}:R:${contentSyncKey}`}
                      />
                    </div>
                  ) : (
                    <RichTextEditor
                      value={currentHtml}
                      onChange={onTipTapChange}
                      placeholder="Use headings (#), bullets (-), images, etc."
                      variant="default"
                      showToolbar={false}
                      onEditorReady={(ed) => {
                        editorRef.current = ed;
                        if (ed && typeof currentHtml === "string" && currentHtml.trim().length > 0) {
                          const empty = ed.getText().trim().length === 0;
                          if (empty) ed.commands.setContent(currentHtml, false);
                        }
                      }}
                      contentKey={`${current}:${contentSyncKey}`}
                    />
                  )
                ) : (
                  <div className="w-full min-h-[250px] rounded-md border border-input bg-muted/30 animate-pulse" />
                )}
              </div>

              {/* NEW: hidden desktop image picker */}
              <input
                type="file"
                accept="image/*"
                ref={imageInputRef}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImageFileSelected(file);
                  e.target.value = '';
                }}
                style={{ display: 'none' }}
              />
            </Card>
          </div>

          {/* Markdown deck editor */}
          <div className="h-full p-6 overflow-auto">
            <Card className="h-full p-4 flex flex-col">
              <div className="text-sm text-muted-foreground mb-2">Markdown deck — edit all slides (separate with ---)</div>
              <Textarea
                className="flex-1 resize-none"
                value={deckMd}
                onChange={(e) => {
                  setDeckMd(e.target.value);
                  setMdRevision((r) => r + 1);
                }}
                placeholder={"# Slide 1\n\n- Bullet\n\n---\n\n# Slide 2\n\n- Bullet"}
              />
            </Card>
          </div>
        </div>
      </div>

      {/* Status bar (saved state) */}
      <MapleWordStatusBar
        editor={editorRef.current}
        wordsFallback={wordsFallback}
        charsFallback={charsFallback}
        isSaving={isSaving}
        autoSaving={autoSaving}
        isSaved={isSaved}
      />

      {/* Presenter Overlay */}
      <MapleSlidePresenter
        open={showPresenter}
        onClose={() => setShowPresenter(false)}
        slidesMd={slidesMd}
        initialIndex={current}
      />

      {/* AI Generator Dialog */}
      <MapleSlideGenerator
        open={showGenerator}
        onClose={() => setShowGenerator(false)}
        onApply={(mode, md) => applyGeneratedDeck(mode, md)}
      />

      {/* Slide Template Modal */}
      <SlideTemplateModal
        open={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onCreate={(md) => insertSlideAfterCurrent(md)}
      />

      {/* File dialogs */}
      <DraggableFileDialog
        isOpen={showFileBrowser}
        onClose={() => setShowFileBrowser(false)}
        onFileSelect={handleFileSelect}
        mode="open"
        acceptedTypes={["application/x-mapleslide", "text/markdown", "text/plain", "application/json"]}
        title="Open Presentation"
        initialPath={usernameReady ? getUserDocumentsPath() : 'C:\\User\\Default\\Documents'}
        homePath={usernameReady ? getUserDocumentsPath() : 'C:\\User\\Default\\Documents'}
      />
      <DraggableFileDialog
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        mode="save"
        initialPath={usernameReady ? getUserDocumentsPath() : 'C:\\User\\Default\\Documents'}
        homePath={usernameReady ? getUserDocumentsPath() : 'C:\\User\\Default\\Documents'}
        title="Save Presentation"
        fileName={saveFileName}
        onFileNameChange={setSaveFileName}
        onSave={handleSaveLocation}
      />
    </div>
  );
};